import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { FeatureFlagsManager } from '../../../../utils/core/feature-flags-manager';
import { permissionChecks } from '../../../../utils/permission-check';
import { isDefined } from '../../../../utils/variables';
import { useCompany } from '../../../state/company-state';
import { useOptionalUserState } from '../../../state/user-state/user-state';
import { reset } from '../../../utils/styled-reset';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Card } from '../../project/overview/card';
import { Stack } from '../../stack/stack';

type CompanyOverviewProps = {
  showTitle?: boolean;
};

export const CompanyOverview = ({ showTitle = true }: CompanyOverviewProps) => {
  const { t } = useTranslation();
  const userState = useOptionalUserState();
  const navigate = useNavigate();
  const company = useCompany();
  const showLicensesCard =
    FeatureFlagsManager.instance.isEnabled('SK-5667-licenses-page') ||
    (userState?.isOrganizationAdmin ?? false);

  const invoiceEmail = company.licenses
    .map((x) => x.invoiceEmail)
    .filter(isDefined)
    .join(', ');

  const invoiceTag = company.licenses
    .map((x) => x.invoiceTag)
    .filter(isDefined)
    .join(', ');

  const invoiceReference = company.licenses
    .map((x) => x.invoiceReference)
    .filter(isDefined)
    .join(', ');

  const infoItems = [
    {
      label: t('overview.orgNumber', { ns: 'companies' }),
      value: company.orgNumber,
    },
    {
      label: t('overview.vatNumber', { ns: 'companies' }),
      value: company.vatNumber,
    },
    {
      label: t('overview.address', { ns: 'companies' }),
      value: company.address,
    },
    {
      label: t('overview.zipCode', { ns: 'companies' }),
      value: company.zipCode,
    },
    {
      label: t('overview.city', { ns: 'companies' }),
      value: company.city,
    },
    {
      label: t('overview.country', { ns: 'companies' }),
      value: typeof company.country === 'string' ? company.country : company.country?.name,
    },
    {
      label: t('overview.invoiceEmail', { ns: 'companies' }),
      value: invoiceEmail,
    },
    {
      label: t('overview.invoiceReference', { ns: 'companies' }),
      value: invoiceReference,
    },
    {
      label: t('overview.invoiceTag', { ns: 'companies' }),
      value: invoiceTag,
    },
  ].filter((item) => isDefined(item.value) && String(item.value).trim().length > 0);

  return (
    <Component>
      <Stack spacing={1.5}>
        {showTitle && (
          <FallbackTitle>
            {t('menu.overview.title', { ns: 'navigation' })} - {company.name}
          </FallbackTitle>
        )}

        <InfoSection>
          <InfoGrid>
            {infoItems.map((item, index) => (
              <LabelledContainer key={`${item.label}-${index}`} text={item.label}>
                <Span>{item.value}</Span>
              </LabelledContainer>
            ))}
          </InfoGrid>
        </InfoSection>

        <CardRow direction="row" spacing={0.5}>
          {showLicensesCard &&
            permissionChecks.hasAccessToView(
              '/companies/:companyId/licenses',
              'view:company_licenses',
            ) && (
              <Card
                big={true}
                disableGoogleTranslate={true}
                icon={'id-card'}
                title={t('menu.licenses.title', { ns: 'navigation' })}
                onClick={() => void navigate(`/companies/${company.id}/licenses`)}
              />
            )}
          {permissionChecks.hasAccessToView(
            '/companies/:companyId/admins',
            'view:company_admins',
          ) && (
            <Card
              big={true}
              disableGoogleTranslate={true}
              icon={'user'}
              title={t('menu.companyAdmins.title', { ns: 'navigation' })}
              onClick={() => void navigate(`/companies/${company.id}/admins`)}
            />
          )}
        </CardRow>
      </Stack>
    </Component>
  );
};

const Component = styled.div`
  ${reset}
`;

const FallbackTitle = styled.h1`
  font-size: 19px;
  font-weight: bold;
  text-transform: uppercase;
`;

// Styling is made to match the other pages in angular.

const InfoSection = styled.section`
  background-color: #fafafa;
  border-radius: 12px;
  padding: 1em;
  max-width: 760px;
  box-shadow:
    0 8px 16px rgba(0, 0, 0, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const Span = styled.label`
  font-weight: 500;
`;

const CardRow = styled(Stack)`
  flex-wrap: wrap;
  gap: 12px;

  > * {
    min-width: 220px;
  }
`;
